import React from 'react';

import { getRouteApi } from '@tanstack/react-router';
import { ALPHA_2_ISO_3166, EMPLOYMENT_INDUSTRY, LOAN_REQUEST_COMMANDS } from 'cpool-constants';
import { useTranslation } from 'react-i18next';

import TitleCombobox from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCombobox.tsx';
import TitleComposedInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleComposedInput.tsx';
import TitleCurrencyInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleCurrencyInput.tsx';
import TitleDateInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleDateInput.tsx';
import TitleInput from '~/features/leadDetails/components/Overview/CustomerData/Employment/inputs/general/TitleInput.tsx';
import { FormCard } from '~/features/leadDetails/components/Overview/CustomerData/shared/FormCard';
import { useAclPermissions } from '~/features/leadDetails/hooks/useAclPermissions';
import { canExecuteApplicantCommand } from '~/features/leadDetails/utils/aclPermissions';

const Independent: React.FC<{ secondApplicant?: boolean }> = ({ secondApplicant }) => {
    const { leadId } = getRouteApi('/_app/leads_/details/$leadId').useParams();
    const { permissions } = useAclPermissions({ loanRequestId: leadId });
    const { t } = useTranslation();
    const employmentTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.EMPLOYMENT';
    const incomeTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.INCOME';
    const expensesTranslationPath = 'CREDIT_WORTHINESS.SECTIONS.EXPENSES';

    const paths = {
        branch: 'beschaeftigung.selbststaendiger.firma.branche',
        companyCity: 'beschaeftigung.selbststaendiger.firma.anschrift.ort',
        companyHouseNumber: 'beschaeftigung.selbststaendiger.firma.anschrift.hausnummer',
        companyLand: 'beschaeftigung.selbststaendiger.firma.anschrift.land',
        companyName: 'beschaeftigung.selbststaendiger.firma.name',
        companyStreet: 'beschaeftigung.selbststaendiger.firma.anschrift.strasse',
        companyZipCode: 'beschaeftigung.selbststaendiger.firma.anschrift.plz',
        expensesDepreciationCurrentYear: 'beschaeftigung.selbststaendiger.abschreibungenLaufendesJahr',
        expensesDepreciationLastYear: 'beschaeftigung.selbststaendiger.abschreibungenLetztesJahr',
        expensesDepreciationThreeYearsAgo: 'beschaeftigung.selbststaendiger.abschreibungenVor3Jahren',
        expensesDepreciationTwoYearsAgo: 'beschaeftigung.selbststaendiger.abschreibungenVor2Jahren',
        expensesTaxesCurrentYear: 'beschaeftigung.selbststaendiger.einkommenssteuerLaufendesJahr',
        expensesTaxesLastYear: 'beschaeftigung.selbststaendiger.einkommenssteuerLetztesJahr',
        expensesTaxesThreeYearsAgo: 'beschaeftigung.selbststaendiger.einkommenssteuerVor3Jahren',
        expensesTaxesTwoYearsAgo: 'beschaeftigung.selbststaendiger.einkommenssteuerVor2Jahren',
        incomeGrossCurrentYear: 'beschaeftigung.selbststaendiger.bruttoEinkommenLaufendesJahr',
        incomeGrossLastYear: 'beschaeftigung.selbststaendiger.bruttoEinkommenLetztesJahr',
        incomeGrossThreeYearsAgo: 'beschaeftigung.selbststaendiger.bruttoEinkommenVor3Jahren',
        incomeGrossTwoYearsAgo: 'beschaeftigung.selbststaendiger.bruttoEinkommenVor2Jahren',
        incomeYearly: 'beschaeftigung.selbststaendiger.nettoeinkommenJaehrlich',
        independentSince: 'beschaeftigung.selbststaendiger.selbststaendigSeit',
        jobTitle: 'beschaeftigung.selbststaendiger.berufsbezeichnung',
    };

    const isDisabled = !canExecuteApplicantCommand({
        applicant: secondApplicant ? 2 : 1,
        commandName: LOAN_REQUEST_COMMANDS.changeApplicantEmployment,
        permissions,
    });

    return (
        <>
            <FormCard data-testid={'independent-sub-form'}>
                <TitleCombobox
                    title={t(`${employmentTranslationPath}.BRANCH`)}
                    name={paths.branch}
                    translationPath={'INDUSTRY'}
                    values={Object.values(EMPLOYMENT_INDUSTRY)}
                    disabled={isDisabled}
                />
                <TitleInput
                    title={t(`${employmentTranslationPath}.EMPLOYMENT_TYPE`)}
                    name={paths.jobTitle}
                    disabled={isDisabled}
                />
                <TitleDateInput
                    title={t(`${employmentTranslationPath}.INDEPENDENT_SINCE`)}
                    name={paths.independentSince}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.INCOME_HEALTH_INSURANCE`)}>
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.NET_INCOME_ANNUAL`)}
                    name={paths.incomeYearly}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.INCOME_GROSS_CURRENT_YEAR`)}
                    name={paths.incomeGrossCurrentYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.INCOME_GROSS_LAST_YEAR`)}
                    name={paths.incomeGrossLastYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.INCOME_GROSS_TWO_YEARS_AGO`)}
                    name={paths.incomeGrossTwoYearsAgo}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${incomeTranslationPath}.INCOME_GROSS_THREE_YEARS_AGO`)}
                    name={paths.incomeGrossThreeYearsAgo}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.EXPENSES`)}>
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.INCOME_TAX_CURRENT_YEAR`)}
                    name={paths.expensesTaxesCurrentYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.INCOME_TAX_LAST_YEAR`)}
                    name={paths.expensesTaxesLastYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.INCOME_TAX_TWO_YEARS_AGO`)}
                    name={paths.expensesTaxesTwoYearsAgo}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.INCOME_TAX_THREE_YEARS_AGO`)}
                    name={paths.expensesTaxesThreeYearsAgo}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.DEPRECIATION_CURRENT_YEAR`)}
                    name={paths.expensesDepreciationCurrentYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.DEPRECIATION_LAST_YEAR`)}
                    name={paths.expensesDepreciationLastYear}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.DEPRECIATION_TWO_YEARS_AGO`)}
                    name={paths.expensesDepreciationTwoYearsAgo}
                    disabled={isDisabled}
                />
                <TitleCurrencyInput
                    title={t(`${expensesTranslationPath}.DEPRECIATION_THREE_YEARS_AGO`)}
                    name={paths.expensesDepreciationThreeYearsAgo}
                    disabled={isDisabled}
                />
            </FormCard>
            <FormCard title={t(`${employmentTranslationPath}.SUBSECTIONS.COMPANY_ADDRESS`)}>
                <TitleInput
                    title={t(`${employmentTranslationPath}.COMPANY_NAME`)}
                    name={paths.companyName}
                    placeholder={t(`${employmentTranslationPath}.COMPANY_NAME`)}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.STREET')}
                    secondTitle={t('ADDRESS.HOUSE_NUMBER')}
                    firstInputName={paths.companyStreet}
                    secondInputName={paths.companyHouseNumber}
                    firstPlaceholder={t('ADDRESS.STREET')}
                    secondPlaceholder={t('ADDRESS.HOUSE_NUMBER_SHORT')}
                    disabled={isDisabled}
                />
                <TitleComposedInput
                    firstTitle={t('ADDRESS.ZIP_CODE')}
                    secondTitle={t('ADDRESS.LOCATION')}
                    firstInputName={paths.companyZipCode}
                    secondInputName={paths.companyCity}
                    firstPlaceholder={t('ADDRESS.ZIP_CODE_SHORT')}
                    secondPlaceholder={t('ADDRESS.LOCATION')}
                    firstKeepDigits={5}
                    swapSize={true}
                    disabled={isDisabled}
                />
                <TitleCombobox
                    title={t('ADDRESS.COUNTRY')}
                    name={paths.companyLand}
                    translationPath={'countries'}
                    values={Object.values(ALPHA_2_ISO_3166)}
                    disabled={isDisabled}
                />
            </FormCard>
        </>
    );
};

export default Independent;
